import { ReactNode } from 'react';

export function DiamondDivider({
  label,
  className = '',
}: {
  label?: ReactNode;
  className?: string;
}) {
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <span className="flex-1 h-px bg-line" />
      {label && (
        <>
          <Diamond />
          <span className="text-[10px] tracking-tracker uppercase text-gold whitespace-nowrap">
            {label}
          </span>
          <Diamond />
        </>
      )}
      {!label && <Diamond />}
      <span className="flex-1 h-px bg-line" />
    </div>
  );
}

/**
 * Thin downward Art-Deco chevron outline (~28x16), gold stroke only.
 * Used in the hero (above "voltereta") and before each "Cómo funciona" step.
 */
/**
 * Art-Decó downward arrow ornament — the EXACT brand asset
 * (volteretarestaurante.com → images/valencia-manhattan/carta-decoration.svg):
 * a slim inner triangle nested in a downward chevron/arrow. Used in the hero
 * and the "cómo funciona" markers. Gold via `currentColor`.
 */
export function Chevron({ className = '' }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 34 39"
      width="24"
      height="27"
      className={`text-[#e2bc4e] ${className}`}
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M16.9996 36.8711L7.7336 0L26.2656 0L16.9996 36.8711ZM9.63479 1.46823L16.9996 30.7779L24.3644 1.46824L9.63479 1.46823Z" />
      <path d="M17 39.0016L0 10.1875L11.2595 10.1875L11.2595 11.6741L2.60261 11.6741L17 36.0835L31.4159 11.6741L22.759 11.6741L22.759 10.1875L34 10.1875L17 39.0016Z" />
    </svg>
  );
}

function Diamond() {
  return (
    <svg
      viewBox="0 0 8 8"
      width="6"
      height="6"
      className="text-gold"
      aria-hidden="true"
    >
      <path d="M4 0 L8 4 L4 8 L0 4 Z" fill="currentColor" />
    </svg>
  );
}

/**
 * Small Art-Deco fan ornament — three concentric arcs converging at a point.
 * Used as a card-top accent (positioned with absolute -translate-y-1/2 + bg ink).
 */
export function FanOrnament({ className = '' }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 56 18"
      className={`mx-auto text-gold ${className}`}
      width="56"
      height="18"
      aria-hidden="true"
    >
      <path
        d="M2 16 L28 2 L54 16"
        stroke="currentColor"
        strokeWidth="0.7"
        fill="none"
      />
      <path
        d="M9 16 L28 5 L47 16"
        stroke="currentColor"
        strokeWidth="0.6"
        fill="none"
        opacity="0.65"
      />
      <path
        d="M16 16 L28 8 L40 16"
        stroke="currentColor"
        strokeWidth="0.5"
        fill="none"
        opacity="0.4"
      />
      <path d="M28 2 L28 16" stroke="currentColor" strokeWidth="0.4" opacity="0.4" />
      <circle cx="28" cy="16" r="1" fill="currentColor" />
    </svg>
  );
}

/**
 * Voltereta wordmark + city sub-line. The display script + spaced caps
 * sub-line is the strongest visual identity — used on the header, ticket
 * card, and email. Uses Italiana for the script (italic display) and
 * Limelight for the venue caps.
 */
export function VolteretaLockup({
  venue,
  className = '',
}: {
  venue: string;
  className?: string;
}) {
  return (
    <div className={`text-center ${className}`}>
      <p className="font-display text-3xl md:text-4xl text-gold leading-none">
        Voltereta
      </p>
      <div className="flex items-center justify-center gap-3 mt-1.5">
        <span className="w-7 h-px bg-gold/70" />
        <span className="font-deco text-[11px] tracking-tracker text-gold/95">
          {venue}
        </span>
        <span className="w-7 h-px bg-gold/70" />
      </div>
    </div>
  );
}

/**
 * Section label with two diamond dots flanking — quieter than the full
 * DiamondDivider. Used inline in cards.
 */
export function SectionEyebrow({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-center justify-center gap-2 text-[10px] tracking-tracker uppercase text-gold">
      <span className="text-[6px]">◆</span>
      <span>{children}</span>
      <span className="text-[6px]">◆</span>
    </div>
  );
}
